import {
  ElementRef,
  FC,
  ForwardedRef,
  forwardRef,
  useEffect,
  useMemo,
} from "react";
import Select from "react-select";
import _uniqueId from "lodash/uniqueId";
import _debounce from "lodash/debounce";

import { getOptionsMaxHeight } from "./dropdown.utils";

import {
  Control,
  DropdownIndicator,
  IndicatorsContainer,
  Input,
  Menu,
  MenuList,
  MultiValue,
  Option as OptionComponent,
  Placeholder,
  ValueContainer,
  Group,
  GroupHeading,
} from "./components";
import { Option, MultiSelectDropdownProps } from "./types";
import { NoOptionsMessage } from "./components/NoOptionsMessage";
import { ClearIndicator } from "./components/ClearIndicator";
import { useDropdown } from "./useDropdown";
import { DropdownContainer } from "./Dropdown";
import { useCounterPill } from "./useCounterPill";

const multiSelectOverrideComponents = {
  ClearIndicator,
  Control,
  DropdownIndicator,
  IndicatorsContainer,
  Group,
  GroupHeading,
  Input,
  Menu,
  MenuList,
  NoOptionsMessage,
  Option: OptionComponent,
  Placeholder,
  MultiValue,
  ValueContainer,
};

export const MultiSelectDropdown: FC<MultiSelectDropdownProps> = forwardRef(
  (
    {
      id,
      testId,
      label,
      labelId,
      variant = "DEFAULT",
      isRequired = false,
      errorMessage = "",
      allowIcons = false,
      options,
      defaultValue,
      value,
      name = "",
      onSelect,
      onDeselect,
      onClear,
      placeholder = "Select...",
      menuPlacement,
      maxOptionsToShow = 8,
      size = "NORMAL",
      styles,
      ...selectProps
    },
    ref: ForwardedRef<ElementRef<typeof Select>>
  ) => {
    const generatedId = useMemo(() => _uniqueId("multi-select-"), []);
    const { selected, setSelected, clearSelected, dropdownProps } = useDropdown(
      {
        defaultValue,
        value,
        name: name || id || generatedId,
        onClear,
        options,
        placeholder,
        variant,
        menuPlacement,
        size,
        ...selectProps,
      }
    );

    const hasSelectedOption = !!selected;

    const {
      pillContainerRef,
      resetHiddenPillLabels,
      renderPillCounter,
      hiddenPillLabels,
    } = useCounterPill();

    useEffect(() => {
      renderPillCounter();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    useEffect(() => {
      // We debounce the resize event to avoid re-rendering
      // the pill on every resize pixel event.
      const debouncedHandleResize = _debounce(() => {
        if (!dropdownProps.menuIsOpen) {
          renderPillCounter();
        }
      }, 250);

      window.addEventListener("resize", debouncedHandleResize);

      return () => {
        window.removeEventListener("resize", debouncedHandleResize);
      };
    }, [renderPillCounter, dropdownProps.menuIsOpen]);

    const handleDeselectOption = (option: Option) => {
      setSelected((prevSelected) =>
        prevSelected?.filter((selectedOption) => selectedOption !== option)
      );
      onDeselect({ deselected: option });
    };

    const handleSelectOption = (option: Option) => {
      setSelected((prevSelected) => [...(prevSelected || []), option]);
      onSelect({ selected: option });
    };

    return (
      <DropdownContainer
        id={id ?? generatedId}
        name={name}
        testId={testId}
        label={label}
        labelId={labelId}
        variant={variant}
        errorMessage={errorMessage}
        isRequired={isRequired}
        allowIcons={allowIcons}
        hasSelectedOption={hasSelectedOption}
      >
        <Select
          {...dropdownProps}
          ref={ref}
          valueContainerChildRef={pillContainerRef}
          hiddenPillLabels={hiddenPillLabels}
          id={id}
          isMulti
          components={multiSelectOverrideComponents}
          closeMenuOnSelect={false}
          maxMenuHeight={getOptionsMaxHeight(maxOptionsToShow, options)}
          value={value}
          onInputChange={(...params) => {
            // Action can be 'input-blur' or 'menu-close'
            // When blurring both events get dispatched
            // We have to care about recalculating the hidden pills
            // in both cases.
            renderPillCounter();
            if (
              selectProps.onInputChange &&
              typeof selectProps.onInputChange === "function"
            ) {
              selectProps.onInputChange(...params);
            }
          }}
          onChange={(value, action) => {
            if (value && action.action === "select-option") {
              const option = action.option as Option;
              if (selected?.includes(option)) {
                handleDeselectOption(option);
              } else {
                handleSelectOption(option);
              }
            }
            // triggered when the user clicks on the x icon
            // on the pills from the stack container
            if (action.action === "deselect-option") {
              const option = action.option as Option;
              handleDeselectOption(option);
            }

            if (onClear && action.action === "clear") {
              clearSelected();
              onClear({ cleared: action.removedValues as Option[] });
              resetHiddenPillLabels();
            }
          }}
          styles={styles}
        />
      </DropdownContainer>
    );
  }
);
